import { MasterTransaction } from '../types';
import { toCents, toDollars } from './utils';

/**
 * ============================================================================
 * UNIFIED TRANSACTION CLASSIFICATION SYSTEM
 * ============================================================================
 * 
 * CRITICAL ARCHITECTURAL CONTRACT:
 * ----------------------------
 * 1. DO NOT WRITE INLINE CUSTOM FILTERS FOR TRANSACTION TYPES OR SUMS IN ANY PAGE.
 *    Any custom evaluation like (tx.source !== 'Calculated') or similar written 
 *    directly inside client views will cause data discrepancies across pages.
 * 2. ALL pages (Dashboard, Payroll Export, Audit Trail, Reconciliation) MUST 
 *    use the helper functions in this file as the sole source of truth.
 * 3. Before making changes, refer to the blueprint located at:
 *    /src/docs/TRANSACTION_LOGIC.md
 * 
 * ============================================================================
 */

export const isPos = (tx: MasterTransaction, allTransactions?: MasterTransaction[]): boolean => {
  if (tx.reportingStatus) {
    const statusLower = tx.reportingStatus.toLowerCase();
    if (statusLower.startsWith('pos_')) return true;
    if (statusLower.startsWith('cash_') || statusLower.startsWith('jar_') || statusLower.startsWith('dispute_')) {
      return false;
    }
  }
  let source = typeof tx.source === 'string' ? tx.source.toLowerCase() : '';
  
  // Look up parent transaction source if we are examining a split pool child
  if ((source === 'splitpool' || source === 'calculated') && tx.parentTxId && Array.isArray(allTransactions)) {
    const parent = allTransactions.find(t => t.id === tx.parentTxId);
    if (parent) {
      source = typeof parent.source === 'string' ? parent.source.toLowerCase() : '';
    }
  }
  
  return source.includes('pos') || source.includes('cc') || source.includes('card');
};

export const isCash = (tx: MasterTransaction, allTransactions?: MasterTransaction[]): boolean => {
  if (tx.reportingStatus) {
    const statusLower = tx.reportingStatus.toLowerCase();
    if (statusLower.startsWith('cash_')) return true;
    if (statusLower.startsWith('pos_') || statusLower.startsWith('jar_') || statusLower.startsWith('dispute_')) {
      return false;
    }
  }
  let source = typeof tx.source === 'string' ? tx.source.toLowerCase() : '';

  // Look up parent transaction source if we are examining a split pool child
  if ((source === 'splitpool' || source === 'calculated') && tx.parentTxId && Array.isArray(allTransactions)) {
    const parent = allTransactions.find(t => t.id === tx.parentTxId);
    if (parent) {
      source = typeof parent.source === 'string' ? parent.source.toLowerCase() : '';
    }
  }

  return source.includes('cash');
};

export const isJar = (tx: MasterTransaction, allTransactions?: MasterTransaction[]): boolean => {
  if (tx.reportingStatus) {
    const statusLower = tx.reportingStatus.toLowerCase();
    if (statusLower.startsWith('jar_')) return true;
    if (statusLower.startsWith('pos_') || statusLower.startsWith('cash_') || statusLower.startsWith('dispute_')) {
      return false;
    }
  }
  let source = typeof tx.source === 'string' ? tx.source.toLowerCase() : '';

  // Look up parent transaction source if we are examining a split pool child
  if ((source === 'splitpool' || source === 'calculated') && tx.parentTxId && Array.isArray(allTransactions)) {
    const parent = allTransactions.find(t => t.id === tx.parentTxId);
    if (parent) {
      source = typeof parent.source === 'string' ? parent.source.toLowerCase() : '';
    }
  }

  return source.includes('jar');
};

export const isDispute = (tx: MasterTransaction, allTransactions?: MasterTransaction[]): boolean => {
  if (tx.reportingStatus) {
    const statusLower = tx.reportingStatus.toLowerCase();
    if (statusLower.startsWith('dispute_')) return true;
    if (statusLower.startsWith('pos_') || statusLower.startsWith('cash_') || statusLower.startsWith('jar_')) {
      return false;
    }
  }
  let source = typeof tx.source === 'string' ? tx.source.toLowerCase() : '';

  // Look up parent transaction source if we are examining a split pool child
  if ((source === 'splitpool' || source === 'calculated') && tx.parentTxId && Array.isArray(allTransactions)) {
    const parent = allTransactions.find(t => t.id === tx.parentTxId);
    if (parent) {
      source = typeof parent.source === 'string' ? parent.source.toLowerCase() : '';
    }
  }

  return source.includes('dispute') || source.includes('disputes');
};

export const isRejected = (tx: MasterTransaction): boolean => {
  const submissionStatus = (tx.submissionStatus || '').toLowerCase().trim();
  const status = ((tx as any).status || '').toLowerCase().trim();
  const allocationStatus = (tx.allocationStatus || '').toLowerCase().trim();
  
  const rejected = submissionStatus === 'rejected' || status === 'rejected' || allocationStatus === 'rejected';
  
  if (tx.id === 'iXpF4ymqOg9tWAo8VznE') {
      console.log(`DEBUG_REJECTED_CHECK: ID ${tx.id}, SubStatus: '${submissionStatus}', Status: '${status}', AllocStatus: '${allocationStatus}', Result: ${rejected}`);
  }
  
  return rejected;
};

export const isSubmitted = (tx: MasterTransaction, allTransactions?: MasterTransaction[]): boolean => {
  // Rejected transactions are not submitted (case-insensitive)
  if (isRejected(tx)) return false;

  // Crucial contract: Every baseline parent transaction (type 'Submitted' or 'Reported') represents the 
  // original gross submitted input, regardless of transitions in reportingStatus (e.g. from *_submitted to *_allocated).
  // This preserves the gross submission totals in dashboards, audit trails, and reconciliation screens.
  if (tx.type === 'Submitted' || tx.type === 'Reported') {
    return true;
  }
  
  // Dispute adjustments are finalized transactions, not "Submitted" gross inputs.
  // They should not be included in gross submitted totals.
  if (tx.source === 'Disputes' && tx.type === 'Adjustment') {
    return false;
  }

  // If already allocated (which are child type: 'Allocated' records), treat as not submitted to prevent double-counting.
  if (isAllocated(tx, allTransactions)) {
    return false;
  }

  if (tx.reportingStatus) {
    return tx.reportingStatus.endsWith('_submitted');
  }
  
  return false;
};
export const isAllocated = (tx: MasterTransaction, allTransactions?: MasterTransaction[]): boolean => {
  // Dispute adjustments are not allocations themselves
  if (tx.source === 'Disputes') return false;

  // Identify if it is a parent/source document (the original container of a pool/split).
  // Cash and Jar tips must be allocated, so their parent transactions (Submitted/Reported) are always source documents.
  // POS transactions are source documents only if they have child allocations (splits).
  const isParentSource = 
    (tx.type === 'Submitted' || tx.type === 'Reported') && 
    (
      isCash(tx, allTransactions) || 
      isJar(tx, allTransactions) ||
      (isPos(tx, allTransactions) && Array.isArray(allTransactions) && allTransactions.some(t => t.parentTxId === tx.id))
    );

  if (isParentSource) {
    return false;
  }

  // Any finalized transaction (except parent sources) is considered allocated
  if (tx.allocationStatus === 'Finalized') return true;

  // If reportingStatus specifies it, that's authoritative
  if (tx.reportingStatus) {
    return tx.reportingStatus.endsWith('_allocated');
  }

  // If no allocation is required, it's effectively allocated.
  if (tx.noAllocationRequired) return true;

  if (tx.type === 'Submitted' || tx.type === 'Reported') return false;

  // Otherwise, if it's explicitly Allocated, it's an allocation.
  // This captures the children.
  return tx.type === 'Allocated';
};

export const getRelatedTransactionIds = (
  transactions: MasterTransaction[],
  rootIds: string[]
): Set<string> => {
  const allRelatedIds = new Set<string>(rootIds);
  
  // Build children lookup map for performance
  const childrenMap = new Map<string, string[]>();
  if (Array.isArray(transactions)) {
    transactions.forEach(t => {
      const parentId = t.parentTxId || t.relatedTransactionId;
      if (parentId) {
        if (!childrenMap.has(parentId)) childrenMap.set(parentId, []);
        childrenMap.get(parentId)?.push(t.id);
      }
    });
  }
  
  const queue = [...rootIds];
  while (queue.length > 0) {
    const currentId = queue.shift()!;
    
    // Find parent
    const transaction = Array.isArray(transactions) ? transactions.find(t => t.id === currentId) : undefined;
    if (transaction) {
      const parentId = transaction.parentTxId || transaction.relatedTransactionId;
      if (parentId && !allRelatedIds.has(parentId)) {
        allRelatedIds.add(parentId);
        queue.push(parentId);
      }
      
      // Find children
      const children = childrenMap.get(currentId) || [];
      children.forEach(childId => {
        if (!allRelatedIds.has(childId)) {
          allRelatedIds.add(childId);
          queue.push(childId);
        }
      });
    }
  }
  
  return allRelatedIds;
};

export const isSubmittedCash = (tx: MasterTransaction, allTransactions?: MasterTransaction[]): boolean => isSubmitted(tx, allTransactions) && isCash(tx, allTransactions);
export const isSubmittedJar = (tx: MasterTransaction, allTransactions?: MasterTransaction[]): boolean => isSubmitted(tx, allTransactions) && isJar(tx, allTransactions);
export const isSubmittedPOS = (tx: MasterTransaction, allTransactions?: MasterTransaction[]): boolean => isSubmitted(tx, allTransactions) && isPos(tx, allTransactions);

export const isDashboardSubmitted = (tx: MasterTransaction, allTransactions?: MasterTransaction[]): boolean => {
  // Rejected or already allocated (non-POS) transactions should not be in Submitted (Gross)
  if (isRejected(tx)) return false;
  if (isAllocated(tx, allTransactions) && !isPos(tx, allTransactions)) return false;
  
  // Dispute adjustments are finalized transactions, not "Submitted" gross inputs.
  // They should not be included in gross submitted totals.
  if (tx.source === 'Disputes' && tx.type === 'Adjustment') {
    return false;
  }

  // Pending disputes (non-adjustments) should be included in dashboard totals.
  if (isDispute(tx, allTransactions) && tx.type !== 'Adjustment') {
    return true;
  }

  return (isSubmitted(tx, allTransactions) || tx.submissionStatus === 'Approved' || tx.submissionStatus === 'Finalized') && tx.source !== 'Calculated';
};

export const getPendingDisputeTotal = (pendingDisputes: any[] = []): number => {
  const cents = pendingDisputes
    .filter(d => d.status === 'Pending')
    .reduce((acc, d) => acc + toCents(d.originalAmount || 0), 0);
  return toDollars(cents);
};

export const isDashboardSubmittedCash = (tx: MasterTransaction, allTransactions?: MasterTransaction[]): boolean => isDashboardSubmitted(tx, allTransactions) && isCash(tx, allTransactions);
export const isDashboardSubmittedJar = (tx: MasterTransaction, allTransactions?: MasterTransaction[]): boolean => isDashboardSubmitted(tx, allTransactions) && isJar(tx, allTransactions);
export const isDashboardSubmittedPOS = (tx: MasterTransaction, allTransactions?: MasterTransaction[]): boolean => isDashboardSubmitted(tx, allTransactions) && isPos(tx, allTransactions);

export const isAllocatedCash = (tx: MasterTransaction, allTransactions?: MasterTransaction[]): boolean => isAllocated(tx, allTransactions) && isCash(tx, allTransactions);
export const isAllocatedJar = (tx: MasterTransaction, allTransactions?: MasterTransaction[]): boolean => isAllocated(tx, allTransactions) && isJar(tx, allTransactions);
export const isAllocatedPOS = (tx: MasterTransaction, allTransactions?: MasterTransaction[]): boolean => isAllocated(tx, allTransactions) && isPos(tx, allTransactions);

export const isAllocatedOrPendingPOS = (tx: MasterTransaction, allTransactions?: MasterTransaction[]): boolean => {
  return isAllocated(tx, allTransactions) || (isDashboardSubmitted(tx, allTransactions) && tx.allocationStatus !== 'Finalized' && !isRejected(tx) && isPos(tx, allTransactions));
};

export const isApprovedOrAllocatedForStaff = (tx: MasterTransaction): boolean => {
  return tx.type === 'Allocated' || (tx.source === 'POS' && tx.submissionStatus === 'Approved');
};

export const isRelevantForApproval = (tx: MasterTransaction, staffId: string, staffEmail: string, allTransactions?: MasterTransaction[]): boolean => {
  const isCorrectStaff = tx.staffId === staffId || (tx.staffId === 'unknown' && tx.email && staffEmail && tx.email.toLowerCase() === staffEmail.toLowerCase());
  if (!isCorrectStaff) return false;

  // Existing inclusion criteria
  if (isAllocated(tx, allTransactions) || isDashboardSubmitted(tx, allTransactions)) {
    return true;
  }

  // Broadening: Explicitly include 'Submitted' type transactions 
  // that have not been approved, rejected, or finalized,
  // ensuring visibility regardless of their intermediate dashboardSubmission status.
  const isSubmittedType = tx.type === 'Submitted';
  const isNotProcessed = tx.submissionStatus !== 'Approved' && 
                         tx.submissionStatus !== 'Finalized' && 
                         !isRejected(tx);

  return isSubmittedType && isNotProcessed;
};

export const isPendingFinalization = (tx: MasterTransaction): boolean => {
  const isSubmittedOrReported = tx.type === 'Submitted' || tx.type === 'Reported';
  return isSubmittedOrReported && tx.allocationStatus !== 'Finalized' && !isRejected(tx);
};

/**
 * Multi-Location Hierarchy Helper:
 * Evaluates whether a transaction matches a target location ID.
 * If locationId is 'all' or empty, all transactions match (consolidated mode).
 */
export const matchesLocation = (tx: MasterTransaction, targetLocationId: string): boolean => {
  if (!targetLocationId || targetLocationId === 'all') return true;
  if (tx.locationId) {
    return tx.locationId === targetLocationId;
  }
  // If transaction has no explicit locationId tag, it belongs to the primary store (loc-downtown or default tenant)
  return targetLocationId === 'loc-downtown' || targetLocationId === tx.tenantId;
};

/**
 * Filters an array of MasterTransactions by target location using matchesLocation.
 */
export const filterTransactionsByLocation = (
  transactions: MasterTransaction[],
  targetLocationId: string
): MasterTransaction[] => {
  if (!targetLocationId || targetLocationId === 'all') return transactions;
  return transactions.filter(tx => matchesLocation(tx, targetLocationId));
};

/**
 * Compound Staff Location Matcher:
 * Evaluates whether a staff member is authorized/assigned to a specific location.
 * Supports primary locationId, multi-store assignedLocationIds, and legacy fallback.
 */
export const matchesStaffLocation = (
  staff: { locationId?: string; assignedLocationIds?: string[]; tenantId?: string },
  targetLocationId: string
): boolean => {
  if (!targetLocationId || targetLocationId === 'all') return true;
  
  // 1. Check primary location
  if (staff.locationId && staff.locationId === targetLocationId) {
    return true;
  }
  
  // 2. Check floating / assigned secondary locations
  if (Array.isArray(staff.assignedLocationIds) && staff.assignedLocationIds.includes(targetLocationId)) {
    return true;
  }
  
  // 3. Fallback for legacy staff with no location tag (defaults to primary store)
  if (!staff.locationId && (targetLocationId === 'loc-downtown' || (staff.tenantId && targetLocationId === staff.tenantId))) {
    return true;
  }
  
  return false;
};
